import React from "react";
import { ArrowRightIcon, ArrowsClockwiseIcon, FileMagnifyingGlassIcon } from "@phosphor-icons/react";
import { Pressable, StyleSheet, View } from "react-native";
import type { ExternalCourse } from "@axio/graphql/generated/graphql";
import AdminTable from "../AdminTable/AdminTable";
import AdminTablePagination from "../AdminTable/AdminTablePagination";
import FilterDropdown from "../../common/TableControls/FilterDropdown/FilterDropdown";
import CustomSearchInput from "../../common/general/CustomSearchInput/CustomSearchInput";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import Tooltip from "../../common/tooltip/Tooltip";
import CourseTermSelect from "./CourseTermSelect";
import type { CourseTermOption } from "./courseTermOptions";

type FilterItem = {
  label: string;
  value: string;
};

type StatusTagTone = "inProgress" | "modernized" | "notStarted" | "default";

const ACTION_TOOLTIP_WRAP_AFTER_LENGTH = 45;

export type CourseModernizerCourseRow = {
  course: ExternalCourse;
  termLabel: string | null;
  statusLabel: string;
  scoreLabel: string;
  canEvaluate: boolean;
  canViewResults: boolean;
  canRerun: boolean;
  activeModernizedCourseId: string | null;
};

type CourseModernizerCoursesTableProps = {
  isAdmin: boolean;
  loading: boolean;
  visibleCourses: CourseModernizerCourseRow[];
  totalItems: number;
  page: number;
  pageSize: number;
  facultyFilter: string;
  facultyFilterDropdownOpen: boolean;
  termFilter: string;
  termFilterDropdownOpen: boolean;
  subjectFilter: string;
  subjectFilterDropdownOpen: boolean;
  programFilter: string;
  programFilterDropdownOpen: boolean;
  searchQuery: string;
  facultyOptions: FilterItem[];
  termItems: FilterItem[];
  termOptions: CourseTermOption[];
  subjectItems: FilterItem[];
  programItems: FilterItem[];
  evaluationDisabledReason: string | null;
  courseTermDropdownCourseId: string | null;
  onFacultyFilterChange: (value: string) => void;
  setFacultyFilterDropdownOpen: (open: boolean) => void;
  onTermFilterChange: (value: string) => void;
  setTermFilterDropdownOpen: (open: boolean) => void;
  onCourseTermDropdownChange: (courseId: string | null) => void;
  onCourseTermChange: (courseId: string, term: string) => void;
  onCourseTermClear: (courseId: string) => void;
  onSubjectFilterChange: (value: string) => void;
  setSubjectFilterDropdownOpen: (open: boolean) => void;
  onProgramFilterChange: (value: string) => void;
  setProgramFilterDropdownOpen: (open: boolean) => void;
  onSearchQueryChange: (value: string) => void;
  onEvaluateCourse: (course: ExternalCourse) => void;
  onViewResults: (courseId: string) => void;
  onRerunCourse: (course: ExternalCourse) => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (pageSize: number) => void;
};

function CourseModernizerCoursesTable({
  isAdmin,
  loading,
  visibleCourses,
  totalItems,
  page,
  pageSize,
  facultyFilter,
  facultyFilterDropdownOpen,
  termFilter,
  termFilterDropdownOpen,
  subjectFilter,
  subjectFilterDropdownOpen,
  programFilter,
  programFilterDropdownOpen,
  searchQuery,
  facultyOptions,
  termItems,
  termOptions,
  subjectItems,
  programItems,
  evaluationDisabledReason,
  courseTermDropdownCourseId,
  onFacultyFilterChange,
  setFacultyFilterDropdownOpen,
  onTermFilterChange,
  setTermFilterDropdownOpen,
  onCourseTermDropdownChange,
  onCourseTermChange,
  onCourseTermClear,
  onSubjectFilterChange,
  setSubjectFilterDropdownOpen,
  onProgramFilterChange,
  setProgramFilterDropdownOpen,
  onSearchQueryChange,
  onEvaluateCourse,
  onViewResults,
  onRerunCourse,
  onPageChange,
  onItemsPerPageChange,
}: CourseModernizerCoursesTableProps) {
  const evaluationActionsDisabled = Boolean(evaluationDisabledReason);

  const getStatusTone = (statusLabel: string): StatusTagTone => {
    if (statusLabel === "In Progress") {
      return "inProgress";
    }
    if (statusLabel === "Modernized") {
      return "modernized";
    }
    if (statusLabel === "Not Started") {
      return "notStarted";
    }
    return "default";
  };

  const getStatusTagStyles = (statusLabel: string) => {
    switch (getStatusTone(statusLabel)) {
      case "inProgress":
        return { tag: styles.inProgressStatusTag, text: styles.inProgressStatusTagText };
      case "modernized":
        return { tag: styles.modernizedStatusTag, text: styles.modernizedStatusTagText };
      case "notStarted":
        return { tag: styles.notStartedStatusTag, text: styles.notStartedStatusTagText };
      case "default":
      default:
        return { tag: styles.defaultStatusTag, text: styles.defaultStatusTagText };
    }
  };

  const renderScore = (scoreLabel: string) => {
    if (scoreLabel === "—") {
      return <CustomText text="—" size="xs" useTranslationText={false} style={styles.noScoreText} />;
    }

    const [scoreValue, maxScore] = scoreLabel.split("/");

    return (
      <View style={styles.scoreValueRow}>
        <CustomText text={scoreValue} size="s" weight="medium" useTranslationText={false} style={styles.scoreText} />
        {maxScore ? (
          <CustomText text={`/${maxScore}`} size="s" useTranslationText={false} style={styles.scoreMaxText} />
        ) : null}
      </View>
    );
  };

  const hasActiveFilters =
    facultyFilter !== "all" ||
    termFilter !== "all" ||
    subjectFilter !== "all" ||
    programFilter !== "all" ||
    searchQuery.trim().length > 0;

  const handleFacultyDropdownOpenChange = (open: boolean) => {
    setFacultyFilterDropdownOpen(open);
    if (open) {
      setTermFilterDropdownOpen(false);
      setSubjectFilterDropdownOpen(false);
      setProgramFilterDropdownOpen(false);
      onCourseTermDropdownChange(null);
    }
  };

  const handleTermDropdownOpenChange = (open: boolean) => {
    setTermFilterDropdownOpen(open);
    if (open) {
      setFacultyFilterDropdownOpen(false);
      setSubjectFilterDropdownOpen(false);
      setProgramFilterDropdownOpen(false);
      onCourseTermDropdownChange(null);
    }
  };

  const handleCourseTermDropdownOpenChange = (courseId: string, open: boolean) => {
    onCourseTermDropdownChange(open ? courseId : null);
    if (open) {
      setFacultyFilterDropdownOpen(false);
      setTermFilterDropdownOpen(false);
      setSubjectFilterDropdownOpen(false);
      setProgramFilterDropdownOpen(false);
    }
  };

  const handleSubjectDropdownOpenChange = (open: boolean) => {
    setSubjectFilterDropdownOpen(open);
    if (open) {
      setFacultyFilterDropdownOpen(false);
      setTermFilterDropdownOpen(false);
      setProgramFilterDropdownOpen(false);
      onCourseTermDropdownChange(null);
    }
  };

  const handleProgramDropdownOpenChange = (open: boolean) => {
    setProgramFilterDropdownOpen(open);
    if (open) {
      setFacultyFilterDropdownOpen(false);
      setTermFilterDropdownOpen(false);
      setSubjectFilterDropdownOpen(false);
      onCourseTermDropdownChange(null);
    }
  };

  const handleClearAll = () => {
    onFacultyFilterChange("all");
    onTermFilterChange("all");
    onSubjectFilterChange("all");
    onProgramFilterChange("all");
    onSearchQueryChange("");
    setFacultyFilterDropdownOpen(false);
    setTermFilterDropdownOpen(false);
    setSubjectFilterDropdownOpen(false);
    setProgramFilterDropdownOpen(false);
    onCourseTermDropdownChange(null);
    onPageChange(1);
  };

  return (
    <AdminTable style={styles.adminTable}>
      <View style={styles.tableControlsRow}>
        <View style={styles.filtersRow}>
          {isAdmin && (
            <FilterDropdown
              filterValue={facultyFilter}
              setFilterValue={onFacultyFilterChange}
              filterDropdownOpen={facultyFilterDropdownOpen}
              setFilterDropdownOpen={handleFacultyDropdownOpenChange}
              filterItems={facultyOptions}
              placeholder="All Faculty"
            />
          )}
          <FilterDropdown
            filterValue={termFilter}
            setFilterValue={onTermFilterChange}
            filterDropdownOpen={termFilterDropdownOpen}
            setFilterDropdownOpen={handleTermDropdownOpenChange}
            filterItems={termItems}
            placeholder="All Terms"
          />
          <FilterDropdown
            filterValue={subjectFilter}
            setFilterValue={onSubjectFilterChange}
            filterDropdownOpen={subjectFilterDropdownOpen}
            setFilterDropdownOpen={handleSubjectDropdownOpenChange}
            filterItems={subjectItems}
            placeholder="All Subjects"
          />
          <FilterDropdown
            filterValue={programFilter}
            setFilterValue={onProgramFilterChange}
            filterDropdownOpen={programFilterDropdownOpen}
            setFilterDropdownOpen={handleProgramDropdownOpenChange}
            filterItems={programItems}
            placeholder="All Programs"
          />
          {hasActiveFilters ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear all filters"
              onPress={handleClearAll}
              style={styles.clearAllButton}>
              <CustomText text="Clear all" size="s" useTranslationText={false} style={styles.clearAllText} />
            </Pressable>
          ) : null}
        </View>
        <View style={styles.searchInputWrapper}>
          <CustomSearchInput
            placeholder={isAdmin ? "Search courses, codes, or faculty" : "Search courses or codes"}
            searchQuery={searchQuery}
            setSearchQuery={onSearchQueryChange}
          />
        </View>
      </View>
      <View style={styles.tableHeaderRow}>
        <CustomText
          text="Course Name"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.courseColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Term"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.termColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Faculty"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.facultyColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Status"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.statusColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Score"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.scoreColumn]}
          useTranslationText={false}
        />
        <CustomText
          text="Actions"
          size="xs"
          weight="bold"
          style={[styles.tableHeaderCell, styles.actionsColumn]}
          useTranslationText={false}
        />
      </View>

      {loading ? (
        <View style={styles.emptyState}>
          <CustomText text="Loading external courses..." size="s" useTranslationText={false} />
        </View>
      ) : visibleCourses.length === 0 ? (
        <View style={styles.emptyState}>
          <CustomText text="No external courses found" size="s" useTranslationText={false} />
        </View>
      ) : (
        visibleCourses.map((row) => {
          const courseTermDropdownOpen = courseTermDropdownCourseId === row.course.id;
          const statusTagStyles = getStatusTagStyles(row.statusLabel);

          return (
            <View key={row.course.id} style={[styles.tableRow, courseTermDropdownOpen && styles.tableRowWithOpenMenu]}>
              <View style={[styles.tableCell, styles.courseColumn]}>
                <CustomText
                  text={row.course.name || "Untitled course"}
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                />
                <CustomText
                  text={row.course.courseCode || row.course.providerCourseId || "-"}
                  size="xs"
                  style={styles.secondaryText}
                  useTranslationText={false}
                />
              </View>
              <View
                style={[styles.tableCell, styles.termColumn, courseTermDropdownOpen && styles.termCellWithOpenMenu]}>
                <CourseTermSelect
                  value={row.termLabel}
                  options={termOptions}
                  open={courseTermDropdownOpen}
                  onOpenChange={(open) => handleCourseTermDropdownOpenChange(row.course.id, open)}
                  onChange={(term) => onCourseTermChange(row.course.id, term)}
                  onClear={() => onCourseTermClear(row.course.id)}
                  variant="row"
                  accessibilityLabel={`Term for ${row.course.name || "course"}`}
                />
              </View>
              <View style={[styles.tableCell, styles.facultyColumn]}>
                <CustomText
                  text={
                    row.course.users
                      ?.map((faculty) => faculty?.name)
                      .filter(Boolean)
                      .join(", ") || "-"
                  }
                  size="s"
                  useTranslationText={false}
                />
              </View>
              <View style={[styles.tableCell, styles.statusColumn]}>
                <View style={[styles.statusTag, statusTagStyles.tag]}>
                  <CustomText
                    text={row.statusLabel}
                    size="s"
                    weight="bold"
                    useTranslationText={false}
                    style={[styles.statusTagText, statusTagStyles.text]}
                  />
                </View>
              </View>
              <View style={[styles.tableCell, styles.scoreColumn]}>{renderScore(row.scoreLabel)}</View>
              <View style={[styles.tableCell, styles.actionsColumn]}>
                <View style={styles.rowActions}>
                  {row.canEvaluate ? (
                    <Tooltip
                      content={evaluationDisabledReason ?? undefined}
                      placement="top"
                      showArrow={false}
                      maxWidth={360}
                      wrapTextAfterLength={ACTION_TOOLTIP_WRAP_AFTER_LENGTH}>
                      <CustomButton
                        text="Evaluate"
                        styleType="primary"
                        size="s"
                        leftIcon={<FileMagnifyingGlassIcon size={12} color="#FFFFFF" weight="bold" />}
                        rightIcon={null}
                        style={styles.evaluateButton}
                        textStyle={styles.evaluateButtonText}
                        useTranslationText={false}
                        disabled={evaluationActionsDisabled}
                        onPress={evaluationActionsDisabled ? undefined : () => onEvaluateCourse(row.course)}
                      />
                    </Tooltip>
                  ) : null}
                  {row.canRerun ? (
                    <Tooltip
                      content={evaluationDisabledReason ?? undefined}
                      placement="top"
                      showArrow={false}
                      maxWidth={360}
                      wrapTextAfterLength={ACTION_TOOLTIP_WRAP_AFTER_LENGTH}>
                      <CustomButton
                        text="Re-run"
                        styleType="lightStroke"
                        size="s"
                        leftIcon={<ArrowsClockwiseIcon size={14} color="#475467" weight="bold" />}
                        rightIcon={null}
                        style={styles.secondaryActionButton}
                        textStyle={styles.secondaryActionButtonText}
                        useTranslationText={false}
                        disabled={evaluationActionsDisabled}
                        onPress={evaluationActionsDisabled ? undefined : () => onRerunCourse(row.course)}
                      />
                    </Tooltip>
                  ) : null}
                  {row.canViewResults && row.activeModernizedCourseId ? (
                    <CustomButton
                      text="View Results"
                      styleType="lightStroke"
                      size="s"
                      leftIcon={null}
                      rightIcon={<ArrowRightIcon size={14} color="#475467" weight="bold" />}
                      style={styles.secondaryActionButton}
                      textStyle={styles.secondaryActionButtonText}
                      useTranslationText={false}
                      onPress={() => onViewResults(row.activeModernizedCourseId)}
                    />
                  ) : null}
                </View>
              </View>
            </View>
          );
        })
      )}
      {!loading && totalItems > 0 && (
        <AdminTablePagination
          currentPage={page}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
          navigationStyle="icons"
          containerStyle={styles.pagination}
        />
      )}
    </AdminTable>
  );
}

const styles = StyleSheet.create({
  adminTable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#EAECF0",
    overflow: "visible",
  },
  tableControlsRow: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    position: "relative",
    zIndex: 100,
    elevation: 100,
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
  },
  filtersRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    flex: 1,
  },
  clearAllButton: {
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  clearAllText: {
    color: "#3E68FE",
  },
  searchInputWrapper: {
    width: 320,
    maxWidth: "40%",
    minWidth: 260,
  },
  tableHeaderRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 10,
    zIndex: 1,
    overflow: "visible",
  },
  tableRowWithOpenMenu: {
    zIndex: 1000,
    elevation: 1000,
  },
  tableHeaderCell: {
    color: "#475467",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    fontSize: 13,
  },
  tableCell: {
    justifyContent: "center",
  },
  termCellWithOpenMenu: {
    zIndex: 1000,
    elevation: 1000,
    overflow: "visible",
  },
  courseColumn: {
    flex: 2.4,
  },
  termColumn: {
    flex: 1.2,
  },
  facultyColumn: {
    flex: 1.8,
  },
  statusColumn: {
    flex: 1.1,
  },
  scoreColumn: {
    flex: 0.8,
  },
  actionsColumn: {
    flex: 1.7,
  },
  secondaryText: {
    color: "#667085",
  },
  statusTag: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "transparent",
    paddingHorizontal: 10,
    paddingVertical: 2,
    alignSelf: "flex-start",
  },
  statusTagText: {
    fontSize: 12,
    lineHeight: 18,
  },
  inProgressStatusTag: {
    backgroundColor: "#FEF0C7",
  },
  inProgressStatusTagText: {
    color: "#7A2E0E",
  },
  modernizedStatusTag: {
    backgroundColor: "#E3FBCC",
  },
  modernizedStatusTagText: {
    color: "#2B5314",
  },
  notStartedStatusTag: {
    backgroundColor: "#F2F4F7",
  },
  notStartedStatusTagText: {
    color: "#101828",
  },
  defaultStatusTag: {
    backgroundColor: "#F2F4F7",
  },
  defaultStatusTagText: {
    color: "#101828",
  },
  scoreValueRow: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  scoreText: {
    color: "#101828",
    fontSize: 14,
    lineHeight: 20,
  },
  scoreMaxText: {
    color: "#667085",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "400",
  },
  noScoreText: {
    color: "#667085",
    fontSize: 12,
    lineHeight: 18,
  },
  rowActions: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },
  evaluateButton: {
    backgroundColor: "#3E68FE",
    borderRadius: 9,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  evaluateButtonText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
  },
  secondaryActionButton: {
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  secondaryActionButtonText: {
    color: "#475467",
    fontSize: 14,
    fontWeight: "600",
  },
  emptyState: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  pagination: {
    marginTop: 0,
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
  },
});

export default CourseModernizerCoursesTable;
